import { inject, Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';


import {Md5} from 'ts-md5';
import { environment } from '../../../environments/environment';
import { firstValueFrom, Observable, of, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ContractStatus, Guest, User } from './users.model';

import { getCookie, setCookie, removeCookie } from 'typescript-cookie';
import { faCropSimple } from '@fortawesome/free-solid-svg-icons';


export function getUsername(email: string): string {
  return Md5.hashStr(email.trim().toLowerCase());
}

function generateRandomString(length:number, charset: string): string {

    var result = '';
    for ( var i = 0; i < length; i++ ) {
        result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
}

export function generatePassword(): string {

  const length = 12 + Math.floor(Math.random() * 8);
  const split = 1 + Math.floor(Math.random() * (length - 1));
  

  const password = 
    generateRandomString(split, 'abcdefghijklmnopqrstuvwxyz') + 
    generateRandomString(length-split, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') ;

  return password ;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private readonly GUEST_USER_COOKIE = 'guest_user';
  private readonly GUEST_TOKEN_COOKIE = 'guest_token';

  readonly http: HttpClient = inject(HttpClient) ; 
 

  public async getUser(forceRefresh: boolean): Promise<User | undefined> {

    console.log("Getting user, forceRefresh=", forceRefresh) ;

    //Try to do this directly from session/Cognito
    const session = await fetchAuthSession({forceRefresh}) ;
    let user = User.buildFromSession(session) ;
    
    if (!user) {
      // Check for guest cookies

      if (forceRefresh) {
        user = await firstValueFrom(this.getUserFromApi() ) ;

        if (user && user.guest) {
          setCookie(this.GUEST_USER_COOKIE, JSON.stringify(user), {
            path: '/',
            sameSite: 'lax',
            secure: location.protocol === 'https:',
          });
        }
      } else {
        const guestUserCookie = getCookie(this.GUEST_USER_COOKIE) ;
        console.log("guestUserCookie", guestUserCookie) ;
        if (guestUserCookie) {
          try {
            user = JSON.parse(guestUserCookie) as User ;
          
          } catch (e) {
            console.error("Error parsing guest user cookie", e) ;
          }
        }
      }
    }

    console.log("returning user", user) ;

    return user ;
  }

  getContractStatus(): Observable<ContractStatus | undefined> {
    return this.http.get<ContractStatus>(`${environment.apiHost}/contracts/mine`) ;
  }

  getPolicy(): Observable<any | undefined> {
    return this.http.get<ContractStatus>(`${environment.apiHost}/policies/mine`) ;
  }

  private getUserFromApi(): Observable<User | undefined> {
    return this.http.get<User>(
      `${environment.apiHost}/users/me`
    ) ;
  }

  public updateUser(user:User): Observable<User | undefined> {
    return this.http.patch<User>(
      `${environment.apiHost}/users`, 
      user
    ).pipe(
      switchMap((result: User) => {
        // Ensure session is updated after user update
        return this.getUser(true) ;
      })
    );
  }

  public postAvatar(img:any): Observable<User | undefined> {

    let fd = new FormData();
    fd.append('image', img);

    return this.http.post<User>(
      environment.apiHost + '/users/me/avatars',
      fd
    ).pipe(
      switchMap((result: User) => {
        // Ensure session is updated after user update
        return this.getUser(true) ;
      })
    );
  }

  public checkAccountExists(email: string): Observable<boolean> {

    return this.http.post<any>(
      `${environment.apiHost}/check-account`,
      { email: email }
    ).pipe(
      switchMap((result: any) => {
        return of(result.exists) ;
      })
    );
  }

  public registerAsGuest(givenName: string, familyName: string): Observable<Guest> {

    console.log("Registering guest", givenName, familyName) ;

    return this.http.post<Guest>(
      `${environment.apiHost}/users/guest`,
      {givenName:givenName, familyName:familyName}
    ).pipe(
      tap((g: Guest) => {
        this.persistGuestCookies(g) ;
      })
    ) ;
  }

  

  private persistGuestCookies(g: Guest): void {
    const expires =  new Date(g.expiresAt as any);
    console.log("Persisting guest cookies", g, expires) ;

    // Token cookie
    setCookie(this.GUEST_TOKEN_COOKIE, g.token, {
      expires: 1,
      path: '/',
      sameSite: 'lax',
      secure: location.protocol === 'https:',
    });

    // User cookie (stringify)
    setCookie(this.GUEST_USER_COOKIE, JSON.stringify(g.user), {
      expires: 1,
      path: '/',
      sameSite: 'lax',
      secure: location.protocol === 'https:',
    });
  }


}
